import React, { useEffect, useMemo, useState } from "react";
import { backendUrl, SUBSCRIPTION_MODULE_TYPE } from "../../constants";

const normalizeHtmlLink = href => {
  if (!href) return null;
  if (/^https?:\/\//i.test(href)) return href;
  if (href.startsWith("//")) return `https:${href}`;
  if (href.startsWith("/")) return `https://www.kimbocorp.com${href}`;
  return `https://${href}`;
};

const sanitizeHtml = value =>
  String(value || "")
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
    .replace(/\son\w+="[^"]*"/gi, "")
    .replace(/\son\w+='[^']*'/gi, "")
    .replace(/javascript:/gi, "");

const capitalizePlanName = value =>
  String(value || "-")
    .replace(/-/g, " ")
    .replace(/\b\w/g, char => char.toUpperCase());

const getPlanActivationYearTotal = plan => {
  const selectedOption = plan?.businessPlanOption;

  return (
    plan?.activationYearTotal ??
    (selectedOption ? plan?.businessPlanPayload?.[selectedOption]?.activationYearTotal : null) ??
    plan?.businessPlanPayload?.planA?.activationYearTotal ??
    plan?.businessPlanPayload?.planB?.activationYearTotal ??
    null
  );
};

const getPlanDisplayPrice = plan => {
  const planPrice = plan?.price;

  if (planPrice !== null && planPrice !== undefined && parseFloat(planPrice || 0) === 0) {
    return getPlanActivationYearTotal(plan) ?? planPrice;
  }

  return planPrice ?? plan?.amount ?? plan?.payableAmount ?? plan?.basePrice ?? null;
};

const getSafePlanDisplayPrice = plan => {
  const displayPrice = Number(getPlanDisplayPrice(plan));
  return Number.isFinite(displayPrice) ? displayPrice : 0;
};

const QUOTE_FROM_BUSINESS_PLAN_KEYS = [
  "WEALTH_RETENTION_LICENSED_MFO",
  "CAPITAL_ACCESS_LICENSED",
  "DIGITAL_ASSETS_LICENSED",
];

const BUSINESS_PLAN_PRICE_MAP = {
  WEALTH_RETENTION_LICENSED_SFO: { fixed: 37000 },
  WEALTH_RETENTION_LICENSED_MFO: { quoteFrom: 65000 },
  CAPITAL_ACCESS_LICENSED: { quoteFrom: 58000 },
  DIGITAL_ASSETS_LICENSED: { quoteFrom: 68000 },
};

const QUOTATION_OPTION_CODES = ["PLAN_A_QUOTATION", "PLAN_B_QUOTATION"];

const normalizePlanText = value =>
  String(value || "")
    .toLowerCase()
    .replace(/[–—]/g, "-")
    .replace(/\s+/g, " ")
    .trim();

const getBusinessPlanCode = plan =>
  plan?.businessPlanCode ?? plan?.businessPlanPayload?.plan?.code ?? plan?.planCode ?? null;

const getPlanText = plan =>
  normalizePlanText([
    plan?.name,
    plan?.title,
    plan?.fullName,
    plan?.businessPlanPayload?.plan?.fullName,
    plan?.businessPlanPayload?.plan?.name,
    plan?.businessPlanOptionCode,
    plan?.businessPlanOption,
  ].filter(Boolean).join(" "));

const getBusinessPlanPriceKey = plan => {
  const code = getBusinessPlanCode(plan);
  const text = getPlanText(plan);

  if (code === "WEALTH_RETENTION_LICENSED") {
    return text.includes("mfo") ? "WEALTH_RETENTION_LICENSED_MFO" : "WEALTH_RETENTION_LICENSED_SFO";
  }

  return code;
};

const isQuotationOption = plan =>
  QUOTATION_OPTION_CODES.includes(plan?.businessPlanOptionCode) ||
  QUOTATION_OPTION_CODES.includes(plan?.businessPlanOption);

const isQuoteFromBusinessPlan = plan =>
  parseInt(plan?.planType?.value || plan?.planType) === SUBSCRIPTION_MODULE_TYPE.CUSTOM &&
  (plan?.activationplan === true || plan?.activationPlan === true) &&
  (QUOTE_FROM_BUSINESS_PLAN_KEYS.includes(getBusinessPlanPriceKey(plan)) || isQuotationOption(plan));

const getQuoteFromBusinessPlanPrice = plan =>
  BUSINESS_PLAN_PRICE_MAP[getBusinessPlanPriceKey(plan)]?.quoteFrom || null;

const getCurrencyCode = plan => {
  const currency = plan?.currency?.value || plan?.currency || "SGD";
  return String(currency).toUpperCase();
};

const formatAmount = (value, decimal = 2) => {
  const amount = parseFloat(value || 0);
  if (Number.isNaN(amount)) return decimal > 0 ? "0.00" : "0";
  return amount.toLocaleString(undefined, { minimumFractionDigits: decimal, maximumFractionDigits: decimal });
};

const getPlanImageUrl = (plan, fallbackImage) => {
  const image =
    plan?.desktopImage?.filename ||
    plan?.desktopImage ||
    plan?.mobileImage?.filename ||
    plan?.mobileImage ||
    plan?.image?.filename ||
    plan?.image;

  if (!image) return fallbackImage;
  if (/^https?:\/\//i.test(image)) return image;
  return `${backendUrl}${image}`;
};

function DetailCheckIcon() {
  return (
    <svg viewBox="0 0 18 18" aria-hidden="true" focusable="false">
      <path d="M7.35 11.48 13.06 5.77c.28-.28.72-.28 1 0s.28.74 0 1.02L7.85 13c-.28.28-.72.28-1 0l-2.92-2.92a.72.72 0 0 1 0-1.02c.28-.28.74-.28 1.02 0l2.4 2.42Z" />
    </svg>
  );
}

function StaticPlanBullet({ children }) {
  return (
    <div className="activate-plan-detail-bullet">
      <DetailCheckIcon />
      <span>{children}</span>
    </div>
  );
}

function StaticPlanSection({ title, description, children }) {
  return (
    <section className="activate-plan-detail-section">
      <h4>{title}</h4>
      {description && <p>{description}</p>}
      {children && <div className="activate-plan-detail-bullets">{children}</div>}
    </section>
  );
}

function HtmlSection({ html }) {
  const safeHtml = useMemo(() => sanitizeHtml(html), [html]);

  const handleClick = event => {
    const link = event.target.closest("a");
    if (!link) return;

    const url = normalizeHtmlLink(link.getAttribute("href"));
    if (!url) return;

    event.preventDefault();
    window.open(url, "_blank", "noopener,noreferrer");
  };

  if (!safeHtml) {
    return <div className="activate-empty-state">No details found.</div>;
  }

  return (
    <section
      className="activate-plan-detail-html activate-plan-detail-section"
      onClick={handleClick}
      dangerouslySetInnerHTML={{ __html: safeHtml }}
    />
  );
}

export default function ActivateBusinessPlanDetail({
  plan,
  fallbackImage,
  isQuotePlan = false,
  onBack,
}) {
  const [activeSection, setActiveSection] = useState(0);
  const planType = parseInt(plan?.planType?.value || plan?.planType);
  const isStaticPlan = planType === SUBSCRIPTION_MODULE_TYPE.STATIC;
  const isPlanQuote = planType === SUBSCRIPTION_MODULE_TYPE.PLAN;
  const quoteFromPrice = getQuoteFromBusinessPlanPrice(plan);
  const shouldShowQuoteFromPrice = isQuoteFromBusinessPlan(plan) && quoteFromPrice;
  const shouldShowQuotePrice = isPlanQuote || isQuotePlan || isQuotationOption(plan);
  const isActivationCustom = planType === SUBSCRIPTION_MODULE_TYPE.CUSTOM && (plan?.activationplan || plan?.activationPlan);
  const shouldShowRegularPrice = !isStaticPlan && !shouldShowQuotePrice && !shouldShowQuoteFromPrice;
  const tabs = isStaticPlan
    ? [
      { key: 0, label: "Highlights" },
      { key: 1, label: "What's Included" },
    ]
    : [
      { key: 0, label: "Key features" },
      ...(isActivationCustom ? [{ key: 2, label: "Pricing" }] : []),
      { key: 1, label: "Highlights" },
    ];
  const price = getSafePlanDisplayPrice(plan);
  const imageUrl = getPlanImageUrl(plan, fallbackImage);

  useEffect(() => {
    setActiveSection(0);
  }, [plan]);

  return (
    <div className="activate-plan-detail">
      <div className="activate-plan-detail-hero">
        <div className="activate-plan-detail-image">
          {imageUrl && <img src={imageUrl} alt="" />}
        </div>
        <div className="activate-plan-detail-summary">
          <h3>{capitalizePlanName(plan?.name)}</h3>
          {plan?.title && <p className="activate-plan-detail-title">{plan.title}</p>}
          {plan?.whoItsFor && <p>{plan.whoItsFor}</p>}
          {shouldShowQuoteFromPrice && (
            <strong>From {getCurrencyCode(plan)} {formatAmount(quoteFromPrice, 0)}</strong>
          )}
          {shouldShowRegularPrice && (
            <strong>{getCurrencyCode(plan)} {formatAmount(price)}</strong>
          )}
          {shouldShowQuotePrice && !shouldShowQuoteFromPrice && <strong>Request a quote</strong>}
        </div>
      </div>

      <div className="activate-plan-detail-tabs">
        {tabs.map(tab => (
          <button
            key={tab.key}
            type="button"
            className={activeSection === tab.key ? "active" : ""}
            onClick={() => setActiveSection(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {isStaticPlan ? (
        <>
          {activeSection === 0 && (
            <div className="activate-plan-detail-content">
              <StaticPlanSection
                title="What is a company plan"
                description="A company plan is a complete setup and operating framework, not just incorporation."
              >
                <p className="activate-plan-detail-subtitle">It helps your business:</p>
                <StaticPlanBullet>Start with the right structure</StaticPlanBullet>
                <StaticPlanBullet>Stay compliant with regulations</StaticPlanBullet>
                <StaticPlanBullet>Access banking and payment solutions</StaticPlanBullet>
                <StaticPlanBullet>Connect with stakeholders and opportunities</StaticPlanBullet>
              </StaticPlanSection>
              <StaticPlanSection
                title="Why kimbocorp"
                description="We do not just register companies. We help you build and operate them."
              >
                <StaticPlanBullet>Structured approach, not one-off setup</StaticPlanBullet>
                <StaticPlanBullet>Integrated platform across people, finance, and compliance</StaticPlanBullet>
                <StaticPlanBullet>Ongoing support as your business grows</StaticPlanBullet>
              </StaticPlanSection>
            </div>
          )}
          {activeSection === 1 && (
            <div className="activate-plan-detail-content">
              <StaticPlanSection title="Setup">
                <StaticPlanBullet>Incorporation of your company</StaticPlanBullet>
                <StaticPlanBullet>Structure aligned to your business goals</StaticPlanBullet>
              </StaticPlanSection>
              <StaticPlanSection title="Operations">
                <StaticPlanBullet>Corporate bank account setup</StaticPlanBullet>
                <StaticPlanBullet>Multi-currency payment capabilities</StaticPlanBullet>
                <StaticPlanBullet>Invoice and transaction support</StaticPlanBullet>
              </StaticPlanSection>
              <StaticPlanSection title="Compliance">
                <StaticPlanBullet>Annual filings and tax reporting</StaticPlanBullet>
                <StaticPlanBullet>Financial accounting</StaticPlanBullet>
                <StaticPlanBullet>Governance and licensing support</StaticPlanBullet>
              </StaticPlanSection>
              <StaticPlanSection title="Growth">
                <StaticPlanBullet>Access to stakeholders and campaigns</StaticPlanBullet>
                <StaticPlanBullet>Ongoing advisory and tax optimisation</StaticPlanBullet>
              </StaticPlanSection>
            </div>
          )}
        </>
      ) : (
        <>
          {activeSection === 0 && <HtmlSection html={plan?.targetAudience} />}
          {activeSection === 1 && (
            <div className="activate-plan-detail-content">
              {plan?.assuredFeature?.length > 0 && (
                <StaticPlanSection title="You are assured of:">
                  {plan.assuredFeature.map((item, index) => (
                    <StaticPlanBullet key={`${item}-${index}`}>{item}</StaticPlanBullet>
                  ))}
                </StaticPlanSection>
              )}
              {plan?.guarentedFeature?.length > 0 && (
                <StaticPlanSection title="You are guaranteed to receive:">
                  {plan.guarentedFeature.map((item, index) => (
                    <StaticPlanBullet key={`${item}-${index}`}>{item}</StaticPlanBullet>
                  ))}
                </StaticPlanSection>
              )}
              {!plan?.assuredFeature?.length && !plan?.guarentedFeature?.length && (
                <div className="activate-empty-state">No highlights found.</div>
              )}
            </div>
          )}
          {activeSection === 2 && <HtmlSection html={plan?.pricingDetail} />}
        </>
      )}

      {plan?.shortDescription && <p className="activate-plan-detail-short">{plan.shortDescription}</p>}

      <div className="activate-actions">
        <button type="button" className="activate-back" onClick={onBack}>
          Back
        </button>
      </div>
    </div>
  );
}
